import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiBriefcase, FiSearch, FiUserCheck, FiX } from "react-icons/fi";
import { apiService } from "../../../api/apiService";
import {
    PUSTE_DLA_KOGO,
    ZRODLO_DZIALU,
    komunikatZrodlaDzialu,
} from "./dlaKogoZapotrzebowania";
import s from "./WyborOsobyDlaKogo.module.css";

/**
 * [DLA KOGO 2026-09-19] Pole „Dla kogo *” w zapotrzebowaniu na sprzęt.
 *
 * Polecenie właściciela: „tutaj wybieramy od razu pracownika z systemu, wpisujemy jego dane lub nie,
 * oraz możemy wskazać dział — tak by to się od razu mapowało.”
 *
 * BYŁO: jedno pole tekstowe „Imię i nazwisko pracownika”. Obsługa dostawała napis — bez konta, bez
 * działu, bez możliwości sprawdzenia, o którego z trzech Kowalskich chodzi.
 *
 * 🔴 OBIE DROGI SĄ WIDOCZNE NARAZ, bez przełącznika. Wybór konta i wpisanie danych ręcznie to nie są
 * dwa tryby, między którymi trzeba się przełączać: wyszukiwarka stoi na górze, pola „Imię i nazwisko”
 * oraz „Dział” pod nią i są edytowalne ZAWSZE. Przełącznik („mam konto / nie mam konta”) oznaczałby,
 * że osoba spoza systemu jest przypadkiem schowanym — a to właśnie dla niej pisze się dane ręcznie.
 *
 * 🔴 KONTROLKA NIE PISZE DO BAZY I NIE ZAKŁADA DZIAŁÓW. Dział jest tekstem opisującym, dla kogo jest
 * sprzęt. Zakładanie brakujących wierszy `Departments` nadawałoby uprawnienia (uprawnienie da się
 * nadać działowi) — patrz komentarz w `dlaKogoZapotrzebowania.js`.
 *
 * 🔴 DLACZEGO NIE `DodatkoweOsoby`. Tamta kontrolka DOPISUJE ludzi do zgłoszenia: wskazane osoby
 * dostają sprawę na własną listę „Moje zgłoszenia” i śledzą jej realizację. „Dla kogo” niczego
 * takiego nie robi i nie może robić — to opis odbiorcy sprzętu, nie nadanie dostępu do sprawy.
 * Wspólna jest tylko wyszukiwarka (`apiService.searchPeople`).
 */
const WyborOsobyDlaKogo = ({ pozycja, onZmiana, idPozycji, blad = null, bladKlasa = "", disabled = false }) => {
    const [fraza, setFraza] = useState("");
    const [wyniki, setWyniki] = useState([]);
    const [szuka, setSzuka] = useState(false);
    const [bladSzukania, setBladSzukania] = useState(false);

    const dane = { ...PUSTE_DLA_KOGO, ...(pozycja || {}) };
    const idNazwy = `dlakogo-nazwa-${idPozycji}`;
    const idDzialu = `dlakogo-dzial-${idPozycji}`;

    useEffect(() => {
        const term = fraza.trim();
        if (term.length < 2) {
            setWyniki([]);
            setBladSzukania(false);
            return undefined;
        }
        let zywy = true;
        setSzuka(true);
        const t = setTimeout(() => {
            apiService
                .searchPeople(term, 10)
                .then((r) => {
                    if (!zywy) return;
                    setWyniki(Array.isArray(r?.data) ? r.data : []);
                    setBladSzukania(false);
                })
                .catch(() => {
                    /* 🔴 Nieudane wyszukanie MUSI się różnić od „nikogo nie znaleziono”. Cicha pustka
                       uczy człowieka, że tej osoby nie ma w systemie — i każe mu wpisać dane ręcznie,
                       choć konto istnieje. */
                    if (!zywy) return;
                    setWyniki([]);
                    setBladSzukania(true);
                })
                .finally(() => {
                    if (zywy) setSzuka(false);
                });
        }, 350);
        return () => {
            zywy = false;
            clearTimeout(t);
        };
    }, [fraza]);

    /** Wybór konta podstawia nazwę I dział — „tak by to się od razu mapowało”. */
    const wybierzKonto = useCallback(
        (u) => {
            const nazwa = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "";
            const dzial = (u.department || "").trim();
            onZmiana({
                forWhom: nazwa,
                forWhomUserId: u.id,
                forWhomDepartment: dzial,
                // Serwer mówi, SKĄD zna dział (przypisanie / książka / nie wie). Gdy nie wie, zostaje
                // etykieta „brak” — i pod polem staje zdanie o tym wprost, zamiast pustego miejsca.
                forWhomDepartmentSource: dzial ? (u.departmentSource || ZRODLO_DZIALU.PRZYPISANIE) : ZRODLO_DZIALU.BRAK,
            });
            setFraza("");
            setWyniki([]);
        },
        [onZmiana]
    );

    const odepnijKonto = useCallback(() => {
        // Zostawiamy wpisaną nazwę i dział — człowiek odpina konto, żeby poprawić dane, a nie po to,
        // żeby zaczynać od zera.
        onZmiana({ forWhomUserId: null });
    }, [onZmiana]);

    const zmienNazwe = useCallback(
        (v) => {
            // Ręczna zmiana nazwy ZRYWA powiązanie z kontem: napis przestaje opisywać wybrane konto,
            // a zgłoszenie z numerem konta „#42” i cudzym nazwiskiem jest gorsze niż samo nazwisko.
            onZmiana(dane.forWhomUserId ? { forWhom: v, forWhomUserId: null } : { forWhom: v });
        },
        [onZmiana, dane.forWhomUserId]
    );

    const zmienDzial = useCallback(
        (v) => onZmiana({ forWhomDepartment: v, forWhomDepartmentSource: ZRODLO_DZIALU.RECZNIE }),
        [onZmiana]
    );

    const komunikatDzialu = komunikatZrodlaDzialu(dane.forWhomDepartmentSource);
    const dzialNieustalony = dane.forWhomDepartmentSource === ZRODLO_DZIALU.BRAK && !dane.forWhomDepartment.trim();

    return (
        <div className={s.blok}>
            <div className={s.naglowek}>
                <span className={s.tytul}>
                    <FiUserCheck size={12} /> Dla kogo <span className={s.req}>*</span>
                </span>
                {dane.forWhomUserId && (
                    <span className={s.pigulka} title={`Konto w aplikacji #${dane.forWhomUserId}`}>
                        <span className={s.pigulkaNazwa}>{dane.forWhom || `konto #${dane.forWhomUserId}`}</span>
                        <button
                            type="button"
                            className={s.pigulkaUsun}
                            onClick={odepnijKonto}
                            disabled={disabled}
                            title="Odepnij konto — dane zostaną jako wpisane ręcznie"
                            aria-label="Odepnij konto"
                        >
                            <FiX size={11} />
                        </button>
                    </span>
                )}
            </div>

            <p className={s.wyjasnienie}>
                Wybierz pracownika z aplikacji — dział podstawi się sam. Osobę spoza systemu wpisz ręcznie
                w polach poniżej.
            </p>

            <div className={s.szukajka}>
                <FiSearch className={s.szukajkaIkona} size={13} aria-hidden="true" />
                <input
                    type="text"
                    className={s.szukajkaInput}
                    value={fraza}
                    onChange={(e) => setFraza(e.target.value)}
                    placeholder="Wyszukaj pracownika po imieniu, nazwisku lub adresie…"
                    disabled={disabled}
                    aria-label="Wyszukaj pracownika w aplikacji"
                    /* 🔴 To pole siedzi w formularzu wysyłanym Enterem. Bez przechwycenia Enter w
                       wyszukiwarce ZAKŁADAŁBY zgłoszenie w trakcie szukania odbiorcy sprzętu. */
                    onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                    }}
                />
            </div>

            {fraza.trim().length >= 2 && (
                <div className={s.wyniki}>
                    {szuka && <div className={s.info}>Szukam…</div>}
                    {!szuka && bladSzukania && (
                        <div className={s.infoBlad}>
                            Nie udało się sprawdzić listy pracowników — spróbuj ponownie albo wpisz dane ręcznie.
                        </div>
                    )}
                    {!szuka && !bladSzukania && wyniki.length === 0 && (
                        <div className={s.info}>Brak kont dla „{fraza.trim()}” — wpisz dane ręcznie poniżej.</div>
                    )}
                    {!szuka &&
                        wyniki.map((u) => (
                            <button
                                key={u.id}
                                type="button"
                                className={s.wynik}
                                onClick={() => wybierzKonto(u)}
                                disabled={disabled}
                            >
                                <span className={s.wynikNazwa}>
                                    {`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email}
                                </span>
                                <span className={s.wynikMeta}>
                                    {u.email}
                                    {u.department ? ` · ${u.department}` : " · dział nieznany"}
                                </span>
                            </button>
                        ))}
                </div>
            )}

            <div className={s.pola}>
                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor={idNazwy}>
                        Imię i nazwisko <span className={s.req}>*</span>
                    </label>
                    <input
                        id={idNazwy}
                        type="text"
                        className={`${s.input} ${bladKlasa}`}
                        value={dane.forWhom}
                        onChange={(e) => zmienNazwe(e.target.value)}
                        placeholder="np. Jan Kowalski"
                        disabled={disabled}
                    />
                    {blad}
                </div>
                <div className={s.pole}>
                    <label className={s.etykieta} htmlFor={idDzialu}>
                        Dział
                    </label>
                    <div className={s.zDzialem}>
                        <FiBriefcase className={s.dzialIkona} size={13} aria-hidden="true" />
                        <input
                            id={idDzialu}
                            type="text"
                            className={s.inputDzial}
                            value={dane.forWhomDepartment}
                            onChange={(e) => zmienDzial(e.target.value)}
                            placeholder="np. Dział Wdrożeń"
                            disabled={disabled}
                        />
                    </div>
                    {komunikatDzialu && (
                        <span className={dzialNieustalony ? s.komunikatUwaga : s.komunikat}>
                            {dzialNieustalony && <FiAlertTriangle size={11} aria-hidden="true" />} {komunikatDzialu}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WyborOsobyDlaKogo;
